/**
 * CARD ENHANCEMENTS - Global Dawn Card Enhancement JavaScript
 * 
 * This file provides enhanced functionality for Dawn's native card-product snippet.
 * Include this file in any section that wants enhanced card functionality.
 * 
 * Usage:
 * <script src="{{ 'card-enhancements.js' | asset_url }}" defer="defer"></script>
 * 
 * Features:
 * - Automatic button injection for "See product" links
 * - Price container reorganization for better layout
 * - Price ordering (sale price on top)
 * - Responsive layout adjustments
 */
if (typeof window.CardEnhancements === 'undefined') {
class CardEnhancements {
  constructor() {
    this.initialized = false;
    this.enhancedCards = new Set(); // Track enhanced cards to avoid duplicate processing
    this.performanceMetrics = {
      startTime: performance.now(),
      cardsEnhanced: 0,
      totalTime: 0
    };
    this.init();
  }

  /**
   * Initialize card enhancements
   */
  init() {
    if (this.initialized) return;

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.enhanceCards();
        this.setupMutationObserver();
      });
    } else {
      this.enhanceCards();
      this.setupMutationObserver();
    }

    this.initialized = true;
  }

  /**
   * Set up MutationObserver to watch for dynamically added content
   */
  setupMutationObserver() {
    // Create observer to watch for new product cards
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldEnhance = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if any added nodes contain card-information elements
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the node itself is a card-information or contains one
              if (node.classList?.contains('card-information') ||
                  node.querySelector?.('.card-information')) {
                shouldEnhance = true;
              }
              // Also check for product-recommendations elements
              if (node.classList?.contains('product-recommendations--loaded') ||
                  node.querySelector?.('.product-recommendations--loaded')) {
                shouldEnhance = true;
              }
            }
          });
        }
      });

      if (shouldEnhance) {
        // Debounce enhancement calls
        clearTimeout(this.enhanceTimeout);
        this.enhanceTimeout = setTimeout(() => {
          this.enhanceCards();
        }, 100);
      }
    });

    // Start observing
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Find and enhance all cards that should be enhanced
   */
  enhanceCards() {
    // Performance optimization: Use requestAnimationFrame for non-blocking execution
    requestAnimationFrame(() => {
      // Find cards in sections that have enhancement classes
      const enhancedSections = document.querySelectorAll('.card-enhanced, .card-enhancements-enabled');

      enhancedSections.forEach(section => {
        const cards = section.querySelectorAll('.card-information');
        cards.forEach(cardInfo => this.enhanceCard(cardInfo));
      });

      // Also enhance cards in specific sections (like best sellers)
      this.enhanceBestSellersCards();
    });
  }

  /**
   * Enhance a specific card
   * @param {Element} cardInfo - The card-information element
   */
  enhanceCard(cardInfo) {
    if (!cardInfo) return;

    // Performance optimization: Check if already enhanced using Set
    // Generate unique ID based on product URL or card position to avoid collisions
    const cardContainer = cardInfo.closest('.card-wrapper, .grid__item, li') || cardInfo;
    const productLink = cardContainer.querySelector('a[href*="/products/"]') || cardContainer.querySelector('a.full-unstyled-link');
    const productUrl = productLink ? productLink.href : '';
    const cardId = productUrl ? productUrl.split('/').pop() : (cardInfo.dataset.cardId || Math.random().toString(36));

    if (this.enhancedCards.has(cardId)) return;

    // Check if already enhanced by DOM structure
    if (cardInfo.querySelector('.price-button-container')) {
      this.enhancedCards.add(cardId);
      return;
    }

    // Get product URL for button (required for all cards)
    // Note: cardContainer and productLink already defined above for ID generation
    const finalProductUrl = productUrl || '#';
    const productTitle = productLink ? productLink.textContent.trim() : 'Product';

    // Skip if no product URL found
    if (finalProductUrl === '#') return;

    const priceElement = cardInfo.querySelector('.price');

    if (priceElement) {
      // Standard case: price element exists
      this.createPriceButtonContainer(cardInfo, priceElement, finalProductUrl, productTitle);
      this.enhancePriceLayout(priceElement);
    } else {
      // Fallback case: no price element (sold-out products might not have prices)
      // Create button without price container
      this.createStandaloneButton(cardInfo, finalProductUrl, productTitle);
    }

    // Track performance and mark as enhanced
    this.enhancedCards.add(cardId);
    this.performanceMetrics.cardsEnhanced++;
  }

  /**
   * Create price-button container and inject button
   * @param {Element} cardInfo - The card information container
   * @param {Element} priceElement - The price element
   * @param {string} productUrl - Product URL for button
   * @param {string} productTitle - Product title for accessibility
   */
  createPriceButtonContainer(cardInfo, priceElement, productUrl, productTitle) {
    // Create container for price and button alignment
    const container = document.createElement('div');
    container.className = 'price-button-container';

    // Create "See product" button
    const button = document.createElement('a');
    button.href = productUrl;
    button.className = 'card__button';
    button.textContent = 'See product';
    button.setAttribute('aria-label', `View ${productTitle}`);

    // Move price into container and add button
    container.appendChild(priceElement);
    container.appendChild(button);
    cardInfo.appendChild(container);

  }

  /**
   * Create standalone button for cards without price elements (e.g., sold-out products)
   * @param {Element} cardInfo - The card information container
   * @param {string} productUrl - Product URL for button
   * @param {string} productTitle - Product title for accessibility
   */
  createStandaloneButton(cardInfo, productUrl, productTitle) {
    // Create container for consistent styling
    const container = document.createElement('div');
    container.className = 'price-button-container price-button-container--standalone';

    // Create "View product" button (different text for sold-out products)
    const button = document.createElement('a');
    button.href = productUrl;
    button.className = 'card__button card__button--standalone';
    button.textContent = 'View product';
    button.setAttribute('aria-label', `View ${productTitle}`);

    container.appendChild(button);
    cardInfo.appendChild(container);
  }

  /**
   * Enhance price layout and ordering
   * @param {Element} priceElement - The price element to enhance
   */
  enhancePriceLayout(priceElement) {
    // Force price layout
    priceElement.style.display = 'flex';
    priceElement.style.flexDirection = 'column';
    priceElement.style.alignItems = 'flex-start';

    const priceRegular = priceElement.querySelector('.price__regular');
    const priceSale = priceElement.querySelector('.price__sale');

    // Determine if this is a sale product by checking if price has sale class
    const isOnSale = priceElement.classList.contains('price--on-sale');

    if (isOnSale && priceSale) {
      // Sale product: Hide regular price section, show only sale section
      if (priceRegular) priceRegular.style.display = 'none';

      // Configure sale section layout
      priceSale.style.display = 'flex';
      priceSale.style.flexDirection = 'column';
      priceSale.style.alignItems = 'flex-start';

      // Ensure correct order: crossed-out regular price first, then sale price
      const saleItem = priceSale.querySelector('.price-item--sale');
      const regularItem = priceSale.querySelector('s.price-item--regular');

      if (saleItem && regularItem) {
        // Get all visible elements (skip visually-hidden spans)
        const visibleElements = Array.from(priceSale.children).filter(child =>
          !child.classList.contains('visually-hidden') && child.textContent.trim()
        );

        // Clear visible price elements and re-add in correct order
        visibleElements.forEach(el => el.remove());

        // Add in desired order: regular (crossed-out) first, then sale price
        priceSale.appendChild(regularItem);
        priceSale.appendChild(saleItem);
      }
    } else {
      // Regular product: Hide sale section, show only regular section
      if (priceSale) priceSale.style.display = 'none';
      if (priceRegular) priceRegular.style.display = 'block';
    }
  }

  /**
   * Enhance cards specifically in best sellers section
   * This maintains backward compatibility with existing best sellers implementation
   */
  enhanceBestSellersCards() {
    const bestSellersSection = document.querySelector('.best-sellers-section');
    if (!bestSellersSection) return;

    // Find all slides in best sellers
    const slides = bestSellersSection.querySelectorAll('[id^="Slide-"]');
    
    slides.forEach(slide => {
      const cardInfo = slide.querySelector('.card-information');
      if (cardInfo) {
        this.enhanceCard(cardInfo);
      }
    });
  }

  /**
   * Enhance cards in a specific section
   * @param {string} sectionSelector - CSS selector for the section
   */
  enhanceSectionCards(sectionSelector) {
    const section = document.querySelector(sectionSelector);
    if (!section) return;

    const cards = section.querySelectorAll('.card-information');
    cards.forEach(cardInfo => this.enhanceCard(cardInfo));
  }

  /**
   * Remove enhancements from cards (useful for cleanup)
   * @param {string} sectionSelector - CSS selector for the section
   */
  removeEnhancements(sectionSelector) {
    const section = document.querySelector(sectionSelector);
    if (!section) return;

    const containers = section.querySelectorAll('.price-button-container');
    containers.forEach(container => {
      const priceElement = container.querySelector('.price');
      const cardInfo = container.parentElement;
      
      if (priceElement && cardInfo) {
        // Move price back to original location
        cardInfo.appendChild(priceElement);
        // Remove the container
        container.remove();
      }
    });
  }

  /**
   * Refresh enhancements (useful after dynamic content changes)
   */
  refresh() {
    this.enhanceCards();
  }

  /**
   * Enhanced method specifically for product recommendations
   * This is called by the ProductRecommendations class after AJAX content loads
   */
  enhanceProductRecommendations(productRecommendationsElement) {
    if (!productRecommendationsElement) return;

    const cards = productRecommendationsElement.querySelectorAll('.card-information');
    let enhancedCount = 0;

    cards.forEach(cardInfo => {
      // Check if already enhanced to avoid duplicates
      if (!cardInfo.querySelector('.price-button-container')) {
        this.enhanceCard(cardInfo);
        enhancedCount++;
      }
    });

    console.log(`Enhanced ${enhancedCount} product recommendation cards`);
    return enhancedCount;
  }

  /**
   * Manual trigger for debugging - can be called from browser console
   */
  debugEnhance() {
    console.log('=== MANUAL CARD ENHANCEMENT DEBUG ===');
    console.log('Enhanced cards so far:', this.enhancedCards.size);
    console.log('Performance metrics:', this.performanceMetrics);

    // Clear previous enhancements for fresh start
    this.enhancedCards.clear();
    this.performanceMetrics.cardsEnhanced = 0;

    // Run enhancement
    this.enhanceCards();
  }

  /**
   * Get performance metrics for debugging
   */
  getPerformanceMetrics() {
    this.performanceMetrics.totalTime = performance.now() - this.performanceMetrics.startTime;
    return {
      ...this.performanceMetrics,
      averageTimePerCard: this.performanceMetrics.cardsEnhanced > 0
        ? this.performanceMetrics.totalTime / this.performanceMetrics.cardsEnhanced
        : 0
    };
  }

  /**
   * Log performance metrics to console (development only)
   */
  logPerformance() {
    if (typeof console !== 'undefined' && console.log) {
      const metrics = this.getPerformanceMetrics();
      console.log('Card Enhancements Performance:', metrics);
    }
  }
}

// Auto-initialize when script loads
const cardEnhancements = new CardEnhancements();

// Make available globally for manual control
window.CardEnhancements = CardEnhancements;
window.cardEnhancements = cardEnhancements;

// Make debug function easily accessible
window.debugCardEnhancements = () => cardEnhancements.debugEnhance();

// Re-enhance cards when new content is loaded (for AJAX/dynamic content)
document.addEventListener('shopify:section:load', () => {
  cardEnhancements.refresh();
});

// Handle theme editor section reloads
document.addEventListener('shopify:section:reorder', () => {
  cardEnhancements.refresh();
});

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CardEnhancements;
}
}